import {
  LAST_SELECTED_BATCH,
  LAST_SELECTED_BATCH_SUBJECT,
  LAST_SELECTED_COHORT_POST_LOGIN,
  LAST_SELECTED_COHORT_PRE_LOGIN,
  REFERRAL_ERROR_MESSAGE,
} from '@/constants/common'
import { BatchDetailModel } from '@/types/batch'
import { LoginEventMetadata } from '@/types/login-event-metadata'
import { getDomainName } from '@/utils/cookie-service'
import { fetchPlatform, isFromApp } from '@/utils/utils'
import toast from 'react-hot-toast'

export const getUserData = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const userData = JSON.parse(
      window.localStorage.getItem('user') || window.localStorage.getItem('USER') || '{}'
    );
    return userData;
  }
  return null;
};

export const getToken = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const userData = window.localStorage.getItem('TOKEN');
    return userData;
  }

  return '';
};

export const getRandomId = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const userData = window.localStorage.getItem('randomId') || window.localStorage.getItem('uuid');
    return userData;
  }

  return '';
};

export const setUserDetails = (response: any) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const user = response?.data?.user;
  const data = response?.data;

  let username = user?.firstName;

  if (user?.lastName) {
    username += ` ${user?.lastName}`;
  }

  document.cookie = `TOKEN_CONTEXT=${JSON.stringify({
    token: data?.access_token,
    username,
    REFRESH_TOKEN: data?.refresh_token,
    expires_in: data?.expires_in,
    randomId: getRandomId() as string,
  })}; expires=${new Date(
    new Date().getTime() + 60 * 60 * 24 * 7 * 1000
  ).toUTCString()}; domain=.${getDomainName()}; path=/;`;

  localStorage.setItem('TOKEN', data?.access_token);
  localStorage.setItem('username', username);
  localStorage.setItem('REFRESH_TOKEN', data?.refresh_token);
  localStorage.setItem('expires_in', data?.expires_in);
};

export function clearLocalStorage() {
  const keysToRemove = [
    'CLIENT_ID',
    'username',
    'countryCode',
    'token',
    'REFRESH_TOKEN',
    'expires_in',
    'user',
    'TOKEN',
    'documentUrl',
    'shouldRegister',
    LAST_SELECTED_BATCH,
    LAST_SELECTED_COHORT_PRE_LOGIN,
    LAST_SELECTED_COHORT_POST_LOGIN,
    LAST_SELECTED_BATCH_SUBJECT,
  ];

  if (typeof window !== 'undefined') {
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }
}

export function deleteAllCookies() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i += 1) {
    document.cookie = `${cookies[i]}=; expires=${new Date(0).toUTCString()}`;
  }
}

export const removeUserDetails = () => {
  clearLocalStorage();
  deleteAllCookies();
};

export function getPageId() {
  const url = typeof window !== 'undefined' ? window?.location?.href : '';

  const urlParts = url.split('/');
  const slug = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2];

  const pageId = slug?.split('?')?.[0];
  return pageId;
}

export const getClass = (selectedClass: string) => {
  return `Class ${selectedClass}`;
};

export const getClassNumber = (classString: string) => {
  return classString.match(/\d+/)?.[0] || '';
};

export const getCustomHeaders = ({ org = 'CJR' }: { org?: 'CJR' | 'PW' }) => {
  const headers: Record<string, any> = {
    Accept: 'application/json, text/plain, /',
    randomId: getRandomId() as string,
    'client-type': 'WEB',
    origin: typeof window !== 'undefined' ? window?.location?.origin : '',
    'client-id': process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID,
    organizationid: process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID,
    'xx-secure': true,
  };
  return headers;
};

export const loadScriptById = (id: string, src: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  return new Promise(resolve => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    document.body.appendChild(script);
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
  });
};

export const findBatchType = (batchDetails: any) => {
  return (batchDetails as any)?.config?.cjrBatchType === 'booster' ? 'booster' : 'master';
};

export const getStartingScheduleOfBatch = (batchDetail: BatchDetailModel) => {
  return batchDetail?.subjects
    ?.map(subject => {
      return subject?.schedules;
    })
    ?.flat()
    ?.sort((a, b) => new Date(a?.startTime).getTime() - new Date(b?.startTime).getTime())?.[0];
};

export const trackPixelEvent = (name: string, data: any) => {
  if (typeof window !== 'undefined') {
    (window as any)?.fbq?.('track', name, data);
  }
};

export function handleReferralError(error: any) {
  if (error?.error?.status === 400) {
    const errorMessage = error?.error?.message;
    if (errorMessage === REFERRAL_ERROR_MESSAGE.REFERAL_MAPPING_EXISTS) {
      toast.error('You have already claimed referral discount.');
    } else if (errorMessage === REFERRAL_ERROR_MESSAGE.APPLICABLE_FOR_NEW_USER) {
      toast.error('Refer & earn is applicable only for new user');
    } else {
      toast.error(errorMessage || 'Failed to apply referral or coupon code. Please try again.');
    }
  } else {
    toast.error('Failed to apply referral or coupon code. Please try again.');
  }
}

export const getTruncatedWords = (text: string = '', wordLimit: number = 10): string => {
  const words = text.split(' ') || [];
  return words.slice(0, wordLimit).join(' ') + (words.length > wordLimit ? '...' : '');
};

export const isIOSDevice = (userAgent?: string, platform?: string): boolean => {
  const ua = userAgent ?? window?.navigator?.userAgent;
  const plat = platform ?? window?.navigator?.platform;

  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      plat
    ) || ua.includes('Mac')
  );
};

export const handleTruecallerAuth = ({
  otplessSigninRef,
  setOTPLessActiveSteps,
  oTPLessActiveSteps,
}: {
  otplessSigninRef: React.MutableRefObject<any>;
  setOTPLessActiveSteps: (val: any) => void;
  oTPLessActiveSteps: {
    script: boolean;
    shouldTriggerTruecaller: boolean;
    userInteracted: boolean;
  };
}) => {
  if (typeof window === 'undefined' || !oTPLessActiveSteps.script) return;

  if (!isIOSDevice()) {
    setOTPLessActiveSteps({
      ...oTPLessActiveSteps,
      shouldTriggerTruecaller: false,
    });
    otplessSigninRef.current?.initiate({
      channel: 'OAUTH',
      channelType: 'TRUE_CALLER',
    });
  }
};

/**
 * Utility function to determine login mode based on OTP verification channel.
 * Returns 'truecaller' if OTP_VERIFICATION_CHANNEL is 'OAUTH', otherwise returns 'otp'.
 */
export const getLoginMode = (): string => {
  if (typeof window === 'undefined') {
    return 'otp';
  }
  return localStorage.getItem('OTP_VERIFICATION_CHANNEL') === 'OAUTH' ? 'truecaller' : 'otp';
};

/**
 * Resolves login source from URL
 */
export const resolveLoginSourceFromUrl = (fullUrl?: string): string => {
  try {
    const urlString =
      fullUrl || (typeof window !== 'undefined' ? window?.location?.href : '') || '';

    if (!urlString) return 'LOGIN';

    const url = new URL(urlString);
    const haystack = `${url.pathname} ${url.search}`.toLowerCase();

    const sourceMapping: Record<string, string> = {
      mlp: 'MLP',
      blp: 'BLP',
      login: 'LOGIN',
      landing: 'LANDING',
      courses: 'COURSES',
      wa_bot_aisensy: 'WA_BOT_AISENSY',
      pw_auto_batch_access: 'PW_AUTO_BATCH_ACCESS',
      app: 'APP',
      admin: 'ADMIN',
    };

    const matchedKey =
      Object.keys(sourceMapping).find(key => haystack.includes(key)) ||
      url.searchParams?.get('came_from')?.toLowerCase();

    return sourceMapping[matchedKey ?? ''] || 'LOGIN';
  } catch {
    return 'LOGIN';
  }
};

/**
 * Determines the 'came_from' value based on the current pathname
 * Returns 'marketing' if pathname includes '/marketing', otherwise returns 'landing'
 */
export const gets2sCameFrom = (): string => {
  if (typeof window === 'undefined') return 'landing';
  return window.location.pathname.includes('/marketing') ? 'marketing' : 'landing';
};

/**
 * Determines the 'source' value based on the current pathname
 * Returns 'MLP' if pathname includes '/marketing', otherwise returns 'LANDING'
 */
export const gets2sSource = (): string => {
  if (typeof window === 'undefined') return 'LANDING';
  return window.location.pathname.includes('/marketing') ? 'MLP' : 'LANDING';
};

/**
 * Builds loginEventMetadata object for login/signup events
 */
export const buildLoginEventMetadata = ({
  came_from,
  page_id,
  selected_cohort,
  source,
  view = 'old',
  device_manufacturer,
  device_model,
  device_os,
  device,
  gclid,
  fbclid,
}: {
  came_from?: string;
  page_id?: string;
  selected_cohort?: string;
  source?: string;
  view?: string;
  device_manufacturer?: string;
  device_model?: string;
  device_os?: string;
  device?: string;
  gclid?: string;
  fbclid?: string;
}): LoginEventMetadata => {
  const utmParams = typeof window !== 'undefined' ? localStorage.getItem('utmParams') : null;
  const parsedUtmParams = utmParams ? JSON.parse(utmParams) : {};

  return {
    came_from: came_from || 'otp_verification',
    login_mode: getLoginMode(),
    page_id: page_id || getPageId() || '',
    platform: isFromApp() ? fetchPlatform() : 'web',
    selected_cohort: selected_cohort || '',
    source: source || resolveLoginSourceFromUrl() || '',
    view: view || 'old',
    gclid: gclid || parsedUtmParams.gclid || '',
    fbclid: fbclid || parsedUtmParams.fbclid || '',
    source_link: typeof window !== 'undefined' ? window.location.href : '',
    device_manufacturer: device_manufacturer || '',
    device_model: device_model || '',
    device_os: device_os || '',
    device: device || '',
  };
};
